import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useTrialGuard from '../hooks/useTrialGuard';
import TrialExpiredModal from './ui/TrialExpiredModal';
import { canAccessRoute } from '../utils/rbac';

/**
 * ProtectedRoute - Checks authentication, license tier, and optional role restrictions.
 * superadminOnly: if true, only superadmin users can access; org_admin and below are redirected.
 * adminOnly: if true, only org_admin+ users can access; non-admins are redirected to /access-denied.
 * Also enforces trial expiry and license-based menu access.
 */
const ProtectedRoute = ({ children, requiredRole = null, screen = null, superadminOnly = false, adminOnly = false }) => {
  const { user, loading, profileLoading, userProfile, isSuperAdmin, isOrgAdmin } = useAuth();
  const location = useLocation();
  const [profileTimedOut, setProfileTimedOut] = useState(false);

  // Trial guard — only runs for org_admins, no-ops for everyone else
  const { blocked: trialBlocked, loading: trialLoading, onActivated } = useTrialGuard();

  // Safety valve: if profileLoading is stuck for more than 8 seconds, unblock the route
  useEffect(() => {
    if (!profileLoading) {
      setProfileTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      setProfileTimedOut(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [profileLoading]);

  // Wait for auth to finish loading first
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no user after loading completes, redirect to login
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // User is authenticated — wait for profile to load (but not forever)
  if (profileLoading && !profileTimedOut) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Superadmin-only pages: redirect org_admin and other non-superadmin roles
  if (superadminOnly && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admin-only pages: redirect non-admins (org_member and below) to access-denied
  if (adminOnly && !isOrgAdmin) {
    return <Navigate to="/access-denied" replace state={{ from: location?.pathname }} />;
  }

  // Trial guard: only block org_admins (not technicians, members, or superadmins)
  const isOrgAdminRole = userProfile?.system_role === 'org_admin';
  if (trialLoading && isOrgAdminRole && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (trialBlocked) {
    return (
      <>
        <div className="min-h-screen bg-gray-50" aria-hidden="true" />
        <TrialExpiredModal isOpen={true} onActivated={onActivated} />
      </>
    );
  }

  // License-based route access check (skip for superadmin)
  if (userProfile && !isSuperAdmin) {
    const routePath = location?.pathname;
    if (!canAccessRoute(userProfile, routePath)) {
      // Redirect to the highest-access page available for their license
      const license = userProfile?.license_type;
      const redirectTo = license === 'basic' ? '/ticket-management' : '/my-dashboard';
      return <Navigate to={redirectTo} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
